/**
 * Wizard draft state — the in-flight inputs the user accumulates as
 * they walk through Upload → Edit → Accounts → Compose → Publish.
 *
 * Two persistence layers:
 *   - The `VideoProject` itself is the source of truth for assets +
 *     timeline + renders. Created in step 1; survives indefinitely.
 *   - The "compose" inputs (description, account picks, schedule, etc.)
 *     live only on the wizard until step 5, when they're flushed into
 *     a real Post. We persist them in localStorage keyed by project id
 *     so a refresh or step-back doesn't lose what the user typed.
 *
 * Once the Post is created and published in step 5 we clear the
 * storage so the wizard never resurrects a stale draft.
 */
export type WizardMode = 'video' | 'photo';

export interface WizardDraft {
  description: string;
  accountIds: number[];
  scheduleFor: string; // local "YYYY-MM-DDTHH:MM" — empty = post immediately
  mode: WizardMode;
  /** Library audio picked on the upload step. Choosing audio turns a
   * single-photo post into a video post; null means no library audio. */
  audioAssetId: number | null;
  audioStart: number; // seconds into the audio file (a saved marker or 0)
  /** Instagram per-account options (wizard mirror of the post form):
   * account ids invited as collaborators / publishing a Story too. */
  igCollaborators: number[];
  igStories: number[];
  /** The ProjectAsset copy made from the library track (use_in_project).
   * Tracked so revisiting the upload step reuses the same copy instead
   * of minting a duplicate, and so the upload tiles can hide it — the
   * music field is its only UI. */
  audioCopy: { libraryId: number; projectAssetId: number } | null;
}

export const STEPS = ['upload', 'edit', 'accounts', 'compose', 'publish'] as const;
export const PHOTO_STEPS = ['upload', 'accounts', 'compose', 'publish'] as const;
export type WizardStep = typeof STEPS[number];

export const STEP_LABELS: Record<WizardStep, string> = {
  upload:   'Add media',
  edit:     'Edit',
  accounts: 'Pick accounts',
  compose:  'Compose',
  publish:  'Publish',
};

export function emptyDraft(): WizardDraft {
  return {
    description: '', accountIds: [], scheduleFor: '', mode: 'video',
    audioAssetId: null, audioStart: 0, audioCopy: null,
    igCollaborators: [], igStories: [],
  };
}

function storageKey(projectId: number): string {
  return `megaphony.wizard.${projectId}`;
}

/* Single key tracking the most recent in-progress wizard. /start
 * checks this first so that landing on the root (which now redirects
 * to /start) resumes an open draft rather than minting a fresh empty
 * VideoProject on every page reload. Cleared on successful publish. */
const CURRENT_KEY = 'megaphony.wizard.current';

export function markCurrent(projectId: number): void {
  try { window.localStorage.setItem(CURRENT_KEY, String(projectId)); }
  catch { /* ignore */ }
}

export function getCurrent(): number | null {
  try {
    const raw = window.localStorage.getItem(CURRENT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function clearCurrent(): void {
  try { window.localStorage.removeItem(CURRENT_KEY); } catch { /* ignore */ }
}

export function loadDraft(projectId: number): WizardDraft {
  try {
    const raw = window.localStorage.getItem(storageKey(projectId));
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as Partial<WizardDraft>;
    return {
      description: parsed.description || '',
      accountIds: Array.isArray(parsed.accountIds) ? parsed.accountIds : [],
      scheduleFor: parsed.scheduleFor || '',
      mode: parsed.mode === 'photo' ? 'photo' : 'video',
      audioAssetId: typeof parsed.audioAssetId === 'number' ? parsed.audioAssetId : null,
      audioStart: typeof parsed.audioStart === 'number' && parsed.audioStart >= 0
        ? parsed.audioStart : 0,
      igCollaborators: Array.isArray(parsed.igCollaborators) ? parsed.igCollaborators : [],
      igStories: Array.isArray(parsed.igStories) ? parsed.igStories : [],
      audioCopy: parsed.audioCopy
        && typeof parsed.audioCopy.libraryId === 'number'
        && typeof parsed.audioCopy.projectAssetId === 'number'
        ? parsed.audioCopy : null,
    };
  } catch {
    return emptyDraft();
  }
}

export function saveDraft(projectId: number, draft: WizardDraft): void {
  try {
    window.localStorage.setItem(storageKey(projectId), JSON.stringify(draft));
  } catch { /* quota / private-mode — wizard still works in-memory */ }
}

export function clearDraft(projectId: number): void {
  try { window.localStorage.removeItem(storageKey(projectId)); } catch { /* ignore */ }
}

export function stepsForMode(mode: WizardMode): readonly WizardStep[] {
  return mode === 'photo' ? PHOTO_STEPS : STEPS;
}

export function stepIndex(step: WizardStep, mode: WizardMode = 'video'): number {
  return stepsForMode(mode).indexOf(step);
}

export function nextStep(step: WizardStep, mode: WizardMode = 'video'): WizardStep | null {
  const steps = stepsForMode(mode);
  const i = steps.indexOf(step);
  return i >= 0 && i < steps.length - 1 ? steps[i + 1] : null;
}

export function prevStep(step: WizardStep, mode: WizardMode = 'video'): WizardStep | null {
  const steps = stepsForMode(mode);
  const i = steps.indexOf(step);
  return i > 0 ? steps[i - 1] : null;
}
